import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import type { PaidAdvisoryDetailDataDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailCard } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailCard";
import { DetailDates } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailDates";
import { DetailLoader } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailLoader";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import { RelatedSectionRedesign } from "web_myplann/Portfolio/Components/Redesign/Detail/RelatedSectionRedesign";
import { PortfolioBasicInfoRows } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/PortfolioBasicInfoRows";
import { getPortfolioFrequencyValue } from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";
import { hasAnyDetailValue } from "web_myplann/Shared/Utils/hasAnyDetailValue";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail.Services;

export const PaidAdvisoryRedesign: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);

	if (isDocumentsLoading) {
		return <DetailLoader />;
	}

	const portfolioData = baseData.data as PaidAdvisoryDetailDataDTO;

	const fixedPrice = portfolioData.fixedPriceByFrequency
		? getPortfolioFrequencyValue(
				portfolioData?.fixedPriceByFrequency ?? 0,
				currencySign,
				portfolioData?.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	return (
		<>
			<DetailCard label={t(Resources.DataCard.BasicInfo.title)}>
				<PortfolioBasicInfoRows baseData={baseData} />
			</DetailCard>
			<DetailDates data={baseData} />

			{hasAnyDetailValue(fixedPrice) && (
				<DetailCard label={t(PageResources.cardLabel)}>
					<DetailRow
						label={t(Resources.PortfolioDetail.PaidAdvisory.fixedPrice)}
						value={fixedPrice}
					/>
				</DetailCard>
			)}

			<RelatedSectionRedesign
				documents={documents?.data}
				related={baseData.related}
				showActions={baseData.allowEditByClient}
				itemKind={PortfolioItemKind.Portfolio}
				portfolioId={baseData.id}
			/>
		</>
	);
};
